import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import TrashBin from '@gravity-ui/icons/TrashBin';
import { Button, Icon, Loader } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { AliceAgentAppImageDTO, uploadAliceAgentAppCover } from 'client/api/services/aliceAgentApps';
import { Field } from 'client/blocks/form/Field/Field';
import { ACCEPTED_LOGO_FORMATS } from 'client/utils/settings/common';
import * as keyset from 'i18n/AliceAgentAppForm';

import { COVER_RESOLUTION } from './AliceAgentAppCoverUploader.constants';
import styles from './AliceAgentAppCoverUploader.module.scss';

const t = i18n(keyset);

interface Props {
    skillId: string;
    coverUrl?: string | null;
    disabled: boolean;
    onUploaded: (image: AliceAgentAppImageDTO) => void;
    onDeleted: () => void;
    onError: (message: string) => void;
}

const renderDropzoneContent = (
    isLoading: boolean,
    isDragActive: boolean,
    userFileName: string | undefined,
): React.ReactNode => {
    if (isLoading) {
        return (
            <div className={clx(styles.tile, styles.tile_loading)}>
                <Loader size="s" />
                <div className={styles.tileLabel}>{t('cover-is-loading')}</div>
            </div>
        );
    }

    if (isDragActive) {
        return (
            <div className={styles.tile}>
                <div>{t('upload-drop-here')}</div>
            </div>
        );
    }

    if (userFileName) {
        return (
            <div className={styles.tile}>
                <div>
                    {userFileName.length > 30 ? `...${userFileName.slice(userFileName.length - 27)}` : userFileName}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.tile}>
            <Icon data={FileArrowUp} size={15} />
            <div className={styles.tileResolution}>{COVER_RESOLUTION}</div>
            <div className={styles.tileLabel}>{t('cover-click-to-load')}</div>
        </div>
    );
};

export const AliceAgentAppCoverUploader: FC<Props> = ({
    skillId,
    coverUrl,
    disabled,
    onUploaded,
    onDeleted,
    onError,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [userFileName, setUserFileName] = useState<string>();

    useEffect(() => {
        if (!userFileName) return undefined;
        const timer = setTimeout(() => setUserFileName(undefined), 1500);
        return () => clearTimeout(timer);
    }, [userFileName]);

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (!acceptedFiles.length) {
                onError(t('upload-format-error'));
                return;
            }
            const file = acceptedFiles[0];
            setIsLoading(true);
            try {
                const image = await uploadAliceAgentAppCover(skillId, file);
                setUserFileName(file.name);
                onUploaded(image);
            } catch (err) {
                const message =
                    err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                        ? err.message
                        : t('cover-upload-error');
                onError(message);
                setUserFileName(undefined);
            } finally {
                setIsLoading(false);
            }
        },
        [skillId, onUploaded, onError],
    );

    const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setUserFileName(undefined);
        onDeleted();
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled: disabled || isLoading,
        accept: ACCEPTED_LOGO_FORMATS,
        multiple: false,
    });

    const canDelete = Boolean(coverUrl) && !isLoading;

    return (
        <div className={styles.container}>
            <Field
                fieldTitle={t('field-cover')}
                fieldDescription={t('field-cover-description')}
                containerClassName={styles.fieldContainer}
                contentClassName={styles.fieldContent}
            >
                <div className={styles.column}>
                    {!disabled && (
                        <div className={styles.loadRow}>
                            <div
                                className={clx(styles.dropzone, isLoading && styles.dropzone_disabled)}
                                {...getRootProps()}
                            >
                                <input {...getInputProps()} />
                                {renderDropzoneContent(isLoading, isDragActive, userFileName)}
                            </div>
                            <Button
                                type="button"
                                view="flat"
                                disabled={!canDelete}
                                onClick={handleDelete}
                                aria-label={t('cover-delete')}
                            >
                                <Icon data={TrashBin} />
                            </Button>
                        </div>
                    )}
                </div>
            </Field>
            {coverUrl && <div className={styles.preview} style={{ backgroundImage: `url("${coverUrl}")` }} />}
        </div>
    );
};
